import { ReactNode, useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  ArtifactFact,
  ArtifactReference,
} from '@/services/types/artifact-digest-types';

import { formatDate } from '../utils/formatters';

interface CardSection {
  label: string;
  content: ReactNode;
}

type ArtifactCardProps = {
  trigger: ReactNode;
  sections: CardSection[];
  popoverWidth?: string;
  rawData?: any;
};

export const ArtifactCard = ({
  trigger,
  sections,
  popoverWidth = 'w-80',
  rawData,
}: ArtifactCardProps) => {
  const [open, setOpen] = useState(false);
  const [showJson, setShowJson] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="py-1.5 border-b border-zinc-100 dark:border-zinc-800">
        <PopoverTrigger asChild>
          <button
            className="w-full text-left text-sm font-medium hover:underline focus:outline-none flex items-center"
            type="button"
          >
            {trigger}
          </button>
        </PopoverTrigger>
      </div>

      <PopoverContent className={`${popoverWidth} p-3`} align="start">
        {rawData && (
          <div className="mb-2 flex justify-end">
            <button
              onClick={() => setShowJson(!showJson)}
              className="text-xs px-2 py-1 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 rounded"
              type="button"
            >
              {showJson ? 'Show Fields' : 'Show JSON'}
            </button>
          </div>
        )}

        {showJson && rawData ? (
          <div className="max-h-[400px] overflow-auto">
            <pre className="text-xs font-mono bg-zinc-50 dark:bg-zinc-900 p-2 rounded-sm overflow-x-auto">
              {JSON.stringify(rawData, null, 2)}
            </pre>
          </div>
        ) : (
          <div className="text-xs space-y-2">
            {sections.map((section, index) => (
              <div key={index}>
                <div className="text-xs font-medium mb-0.5">
                  {section.label}
                </div>
                <div className="text-muted-foreground">{section.content}</div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const FactCard = ({ fact }: { fact: ArtifactFact }) => {
  const sections = [
    {
      label: 'Attribution',
      content: fact.attribution,
    },
    {
      label: 'ID',
      content: (
        <span className="font-mono text-[10px] break-all">{fact.id}</span>
      ),
    },
  ];

  if (fact.supporting_excerpts && fact.supporting_excerpts.length > 0) {
    sections.push({
      label: `Supporting Excerpts (${fact.supporting_excerpts.length})`,
      content: (
        <div className="space-y-1.5">
          {fact.supporting_excerpts.map((excerpt, index) => (
            <div
              key={index}
              className="p-1.5 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-sm italic"
            >
              &ldquo;{excerpt}&rdquo;
            </div>
          ))}
        </div>
      ),
    });
  }

  return (
    <ArtifactCard trigger={fact.statement} sections={sections} rawData={fact} />
  );
};

export const ReferenceCard = ({
  reference,
}: {
  reference: ArtifactReference;
}) => {
  const sections = [
    {
      label: 'Description',
      content: reference.description,
    },
    {
      label: 'Created',
      content: formatDate(reference.created_at),
    },
    {
      label: 'ID',
      content: (
        <span className="font-mono text-2xs break-all">{reference.id}</span>
      ),
    },
  ];

  const trigger = (
    <>
      <span className="font-mono">{reference.value}</span>
      <span className="ml-1.5 text-xs bg-zinc-100 dark:bg-zinc-800 px-1 py-0.5 rounded text-muted-foreground">
        {reference.type}
      </span>
    </>
  );

  return (
    <ArtifactCard trigger={trigger} sections={sections} rawData={reference} />
  );
};
